import React, {
  useState,
  useEffect,
  useCallback,
  Suspense,
  ErrorInfo,
  ReactNode,
} from "react";
import {
  Page,
  PageState,
  ImageEdits,
  Puzzle,
  DEFAULT_THEME_COLOR,
} from "./types";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { Keyboard } from "@capacitor/keyboard";

import HomePage from "./pages/HomePage";
import SearchPage from "./pages/SearchPage";
import CategoriesPage from "./pages/CategoriesPage";
import FeaturedBrandsPage from "./pages/FeaturedBrandsPage";
import NotificationsPage from "./pages/NotificationsPage";
import GroupsPage from "./pages/GroupsPage";
import ProfilePage from "./pages/ProfilePage";
import SettingsPage from "./pages/SettingsPage";
import PostDetailPage from "./pages/PostDetailPage";
import ChatListPage from "./pages/ChatListPage";
import ChatConversationPage from "./pages/ChatConversationPage";
import FilteredFeedPage from "./pages/FilteredFeedPage";
import GameDetailPage from "./pages/GameDetailPage";
import FriendsListPage from "./pages/FriendsListPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import ClueStorePage from "./pages/ClueStorePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import AdminPage from "./pages/AdminPage";
import DailyChallengePage from "./pages/DailyChallengePage";
import RecommendedGroupsPage from "./pages/RecommendedGroupsPage";
import BlockedUsersPage from "./pages/BlockedUsersPage";
import PrivateGamePage from "./pages/PrivateGamePage";
import AIHomePage from "./pages/AIHomePage";
import AISupportChatPage from "./pages/AISupportChatPage";
import CreatePostPage from "./pages/CreatePostPage";
import AboutUsPage from "./pages/AboutUsPage";
import AICreditStorePage from "./pages/AICreditStorePage";
import InviteFriendsPage from "./pages/InviteFriendsPage";
import ManageGroupPage from "./pages/ManageGroupPage";
import ImageEditorPage from "./pages/ImageEditorPage";

import Header from "./components/Header";
import BottomNav from "./components/BottomNav";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import LandingPage from "./pages/LandingPage";

import { AuthProvider, useAuth } from "./AuthContext";
import { PuzzleProvider } from "./PuzzleContext";
import { AdminProvider } from "./AdminContext";
import { AppProvider, useAppContext } from "./AppContext";
import { ChatProvider } from "./ChatContext";
import { PageContext } from "./PageContext";
import { AdService } from "./AdService"; // Import AdService
import { NotificationService } from "./NotificationService"; // Import NotificationService
import BannerAd from "./components/BannerAd";
import { ToastProvider } from "./ToastContext";
import ToastContainer from "./components/ToastContainer";

const EditProfileModal = React.lazy(
  () => import("./components/EditProfileModal")
);
const CelebrationModal = React.lazy(
  () => import("./components/CelebrationModal")
);
const GuessersModal = React.lazy(() => import("./components/GuessersModal"));
const OnboardingTour = React.lazy(() => import("./components/OnboardingTour"));
const DebugConsole = React.lazy(() => import("./components/DebugConsole"));

interface ErrorBoundaryProps {
  children?: ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Root UI Crash:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6 text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">
            Something went wrong.
          </h1>
          <button
            onClick={() => window.location.reload()}
            className="bg-purple-600 text-white font-bold py-2 px-6 rounded-lg shadow-md hover:bg-purple-700 transition-colors"
          >
            Reload App
          </button>
        </div>
      );
    }
    return (this as any).props.children;
  }
}

import GlobalAdModals from "./components/GlobalAdModals";
import SplashScreenComponent from "./components/SplashScreen";

const AppContent: React.FC = () => {
  const { user, loading, userProfile, markOnboardingComplete } = useAuth();
  const { puzzlesSolvedCount, incrementPuzzlesSolvedCount } = useAppContext();

  // Check if running on a native device (Android/iOS)
  const isNative = Capacitor.isNativePlatform();

  // Initialize Ads
  useEffect(() => {
    AdService.initialize().then(() => {
      // Ads are now handled by the BannerAd component or local page logic
    });
  }, []);

  // Show Interstitial Ad every 3 puzzles solved
  useEffect(() => {
    if (puzzlesSolvedCount > 0 && puzzlesSolvedCount % 3 === 0) {
      AdService.showInterstitialAd();
    }
  }, [puzzlesSolvedCount]);

  // Default to Landing page on Web, Login on Native
  // Initialize state based on URL path to support direct links (e.g. for Privacy Policy)
  const [pageStack, setPageStack] = useState<PageState[]>(() => {
    const path = window.location.pathname;
    if (path === "/privacy")
      return [{ page: Page.PrivacyPolicy, title: "Privacy Policy" }];
    if (path === "/terms")
      return [{ page: Page.TermsOfService, title: "Terms of Service" }];
    if (path === "/delete-account")
      return [
        {
          page: Page.Settings,
          title: "Settings",
          props: { autoTriggerDelete: true },
        },
      ];

    return [
      {
        page: isNative ? Page.Login : Page.Landing,
        title: isNative ? "Sign In" : "Welcome",
      },
    ];
  });

  const [isEditProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [editProfileTriggerRect, setEditProfileTriggerRect] =
    useState<DOMRect | null>(null);
  const [celebrationProps, setCelebrationProps] = useState<{
    puzzle: Puzzle;
    suggestions?: Puzzle[];
  } | null>(null);
  const [guessersModalProps, setGuessersModalProps] = useState<{
    title: string;
    userIds: string[];
    extraInfo?: { userId: string, subText: string }[];
  } | null>(null);
  const [isOnboardingOpen, setOnboardingOpen] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Persistent scroll positions for main navigation tabs
  const tabScrollPositions = React.useRef<Record<string, number>>({});

  // Splash Screen Management - Hide only when auth is ready
  useEffect(() => {
    if (!loading && isNative) {
      // Give a tiny extra buffer for the UI to paint and for the page sync to settle
      const timer = setTimeout(() => {
        SplashScreen.hide({
          fadeOutDuration: 400,
        }).catch(() => {});

        // Allow the React Splash to finish or at least stay long enough to be seen
        setTimeout(() => setIsReady(true), 1000);
      }, 300);
      return () => clearTimeout(timer);
    } else if (!loading && !isNative) {
      setIsReady(true);
    }
  }, [loading, isNative]);

  // AUTH SYNC: When logic confirms no user, jump to appropriate entry screen
  useEffect(() => {
    if (loading) return;

    const currentPath = pageStack[pageStack.length - 1];
    const current = currentPath?.page;

    if (user) {
      // If logged in and on a guest page, go Home
      if (
        !current ||
        [Page.Landing, Page.Login, Page.Signup].includes(current)
      ) {
        // If special case for delete account, we stay on settings, otherwise go Home
        if (window.location.pathname === "/delete-account") {
          // Do nothing, let the initial state handle it
        } else {
          setPageStack([{ page: Page.Home, title: "Home" }]);
        }
      }
    } else {
      // If logged out and not on a public page, go to Landing (Web) or Login (App)
      const publicPages = [
        Page.Landing,
        Page.Login,
        Page.Signup,
        Page.PrivacyPolicy,
        Page.TermsOfService,
        Page.AboutUs,
      ];
      if (!current || !publicPages.includes(current)) {
        // Special handling for delete-account when logged out -> force Login
        if (window.location.pathname === "/delete-account") {
          setPageStack([{ page: Page.Login, title: "Sign In" }]);
        } else {
          console.log("Root App: Syncing Logout -> Entry Screen");
          if (isNative) {
            setPageStack([{ page: Page.Login, title: "Sign In" }]);
          } else {
            setPageStack([{ page: Page.Landing, title: "Welcome" }]);
          }
        }
      }
    }
  }, [user, loading, pageStack.length, isNative]);

  // Initialize Push Notifications once when user logs in
  useEffect(() => {
    if (user && isNative) {
      NotificationService.initialize(user.uid);
    }
  }, [user, isNative]);

  useEffect(() => {
    if (
      user &&
      userProfile &&
      !loading &&
      userProfile.hasCompletedOnboarding === false
    ) {
      setOnboardingOpen(true);
    }
  }, [user, userProfile, loading]);

  const switchPage = useCallback((page: Page, title: string, props?: any) => {
    const currentScroll = mainRef.current?.scrollTop || 0;
    const mainNavPages = [
      Page.Home,
      Page.Search,
      Page.Categories,
      Page.Profile,
      Page.Groups,
      Page.Notifications,
    ];

    setPageStack((prevStack) => {
      const currentPage = prevStack[prevStack.length - 1];

      // Save current scroll if leaving a main tab
      if (currentPage && mainNavPages.includes(currentPage.page)) {
        tabScrollPositions.current[currentPage.page] = currentScroll;
      } else if (prevStack.length > 1) {
        // Also update scroll position in stack for stack-based navigation back/forward
        prevStack[prevStack.length - 1].scrollPosition = currentScroll;
      }

      if (mainNavPages.includes(page)) {
        // For main navigation tabs, we reset the stack to avoid infinite growth
        // But we restore the saved scroll position
        const restoredScroll = tabScrollPositions.current[page] || 0;
        return [{ page, title, props, scrollPosition: restoredScroll }];
      } else {
        if (
          currentPage &&
          currentPage.page === page &&
          currentPage.props?.puzzle?.id === props?.puzzle?.id &&
          currentPage.props?.userId === props?.userId
        ) {
          return prevStack;
        }
        return [...prevStack, { page, title, props }];
      }
    });
  }, []);

  const scrollToTop = useCallback((smooth = true) => {
    if (mainRef.current) {
      mainRef.current.scrollTo({
        top: 0,
        behavior: smooth ? "smooth" : "auto",
      });

      // Immediately update the current page's internal scrollPosition
      // so persistence works if navigate away right after scroll to top
      setPageStack((prev) => {
        const newStack = [...prev];
        if (newStack.length > 0) {
          newStack[newStack.length - 1].scrollPosition = 0;

          // Also update tab scroll cache if it's a tab
          const page = newStack[newStack.length - 1].page;
          const mainNavPages = [
            Page.Home,
            Page.Search,
            Page.Categories,
            Page.Profile,
            Page.Groups,
            Page.Notifications,
          ];
          if (mainNavPages.includes(page)) {
            tabScrollPositions.current[page] = 0;
          }
        }
        return newStack;
      });
    }
  }, []);

  const goBack = useCallback(() => {
    setPageStack((prev) =>
      prev.length > 1 ? prev.slice(0, prev.length - 1) : prev
    );
  }, []);

  // Handle Hardware Back Button (Android)
  useEffect(() => {
    const handleBackButton = (event: any) => {
      if (isEditProfileModalOpen) {
        setEditProfileModalOpen(false);
        return;
      }
      if (celebrationProps) {
        setCelebrationProps(null);
        return;
      }
      if (guessersModalProps) {
        setGuessersModalProps(null);
        return;
      }
      if (isOnboardingOpen) {
        setOnboardingOpen(false);
        return;
      }

      if (pageStack.length > 1) {
        goBack();
      } else {
        CapacitorApp.minimizeApp();
      }
    };

    const listener = CapacitorApp.addListener("backButton", handleBackButton);
    return () => {
      listener.then((handler) => handler.remove());
    };
  }, [
    pageStack,
    isEditProfileModalOpen,
    celebrationProps,
    guessersModalProps,
    isOnboardingOpen,
    goBack,
  ]);

  const launchImageEditor = useCallback(
    (
      type: "avatar" | "background" | "post",
      url: string,
      cur: ImageEdits,
      shape: "circle" | "square",
      save: any,
      rect: any,
      ctx?: any
    ) => {
      switchPage(Page.ImageEditor, "Post Editor", {
        imageUrl: url,
        currentEdits: cur,
        previewShape: shape,
        onSave: save,
        postContext: ctx,
      });
    },
    [switchPage]
  );

  const updatePageStackProps = useCallback((index: number, newProps: any) => {
    setPageStack((prev) => {
      const newStack = [...prev];
      let targetIndex = index;
      if (targetIndex < 0) targetIndex = newStack.length + targetIndex;
      if (targetIndex >= 0 && targetIndex < newStack.length) {
        newStack[targetIndex] = {
          ...newStack[targetIndex],
          props: { ...newStack[targetIndex].props, ...newProps },
        };
      }
      return newStack;
    });
  }, []);

  const launchEditProfileModal = useCallback((rect: DOMRect) => {
    setEditProfileTriggerRect(rect);
    setEditProfileModalOpen(true);
  }, []);

  const launchCelebrationModal = useCallback(
    (puzzle: Puzzle, suggestions?: Puzzle[]) => {
      setCelebrationProps({ puzzle, suggestions });
      incrementPuzzlesSolvedCount();
    },
    [incrementPuzzlesSolvedCount]
  );

  const launchGuessersModal = useCallback(
    (title: string, userIds: string[], extraInfo?: { userId: string, subText: string }[]) =>
      setGuessersModalProps({ title, userIds, extraInfo }),
    []
  );

  const currentPageState = pageStack[pageStack.length - 1] || {
    page: isNative ? Page.Login : Page.Landing,
    title: isNative ? "Sign In" : "Welcome",
  };
  const { page, title, props } = currentPageState;

  const mainRef = React.useRef<HTMLElement>(null);

  // Scroll to top or restored position when page changes
  useEffect(() => {
    if (mainRef.current) {
      const targetScroll = currentPageState.scrollPosition || 0;
      // Use a small timeout to ensure DOM has updated before scrolling
      setTimeout(() => {
        mainRef.current?.scrollTo(0, targetScroll);
      }, 10);
    }
  }, [page, props, currentPageState.scrollPosition]);

  const isFullScreen = [
    Page.Landing,
    Page.Login,
    Page.Signup,
    Page.InviteFriends,
    Page.ChatConversation,
    Page.AISupport,
    Page.CreatePost,
    Page.ManageGroup,
    Page.ImageEditor,
  ].includes(page);

  // Manage Banner Visibility based on Page and Keyboard
  useEffect(() => {
    if (isNative) {
      if (isFullScreen || isKeyboardVisible) {
        AdService.hideBanner();
      } else {
        AdService.showBanner();
      }
    }
  }, [isFullScreen, isNative, isKeyboardVisible]);

  // Handle App Resume (Native)
  useEffect(() => {
    if (!isNative) return;

    const onResume = () => {
      if (!isFullScreen && !isKeyboardVisible) {
        console.log(
          "App Content: Resume detected, ensuring banner is visible..."
        );
        AdService.showBanner(); // Try to resume/show without forcing recreation
      }
    };

    // Standard App State Change from Capacitor
    const appStateListener = CapacitorApp.addListener(
      "appStateChange",
      ({ isActive }) => {
        if (isActive) onResume();
      }
    );

    // Custom event from AdService (as a backup)
    document.addEventListener("app-resumed", onResume);

    return () => {
      appStateListener.then((l) => l.remove());
      document.removeEventListener("app-resumed", onResume);
    };
  }, [isNative, isFullScreen, isKeyboardVisible]);

  // Keyboard Listeners
  useEffect(() => {
    if (isNative) {
      const showListener = Keyboard.addListener("keyboardWillShow", () => {
        setIsKeyboardVisible(true);
      });
      const didShowListener = Keyboard.addListener("keyboardDidShow", () => {
        setIsKeyboardVisible(true);
      });
      const hideListener = Keyboard.addListener("keyboardWillHide", () => {
        setIsKeyboardVisible(false);
      });
      const didHideListener = Keyboard.addListener("keyboardDidHide", () => {
        setIsKeyboardVisible(false);
      });

      return () => {
        showListener.then((l) => l.remove());
        didShowListener.then((l) => l.remove());
        hideListener.then((l) => l.remove());
        didHideListener.then((l) => l.remove());
      };
    }
  }, [isNative]);

  const renderPage = () => {
    if (!isReady && isNative) return null; // Wait for readiness on native

    switch (page) {
      case Page.Landing:
        return <LandingPage />;
      case Page.Login:
        return <LoginPage />;
      case Page.Signup:
        return <SignupPage />;
      case Page.Home:
        return <HomePage />;
      case Page.Search:
        return <SearchPage />;
      case Page.Categories:
        return <CategoriesPage />;
      case Page.FeaturedBrands:
        return <FeaturedBrandsPage />;
      case Page.Notifications:
        return <NotificationsPage />;
      case Page.Groups:
        return <GroupsPage />;
      case Page.Profile:
        return <ProfilePage userId={props?.userId} />;
      case Page.Settings:
        return <SettingsPage {...props} />;
      case Page.PostDetail:
        return <PostDetailPage puzzle={props?.puzzle} />;
      case Page.ChatList:
        return <ChatListPage />;
      case Page.ChatConversation:
        return <ChatConversationPage recipient={props?.recipient} />;
      case Page.FilteredFeed:
        return (
          <FilteredFeedPage
            category={props?.category}
            hashtag={props?.hashtag}
          />
        );
      case Page.GameDetail:
        return <GameDetailPage gameId={props?.gameId} />;
      case Page.FriendsList:
        return <FriendsListPage initialTab={props?.initialTab} />;
      case Page.Leaderboard:
        return <LeaderboardPage />;
      case Page.ClueStore:
        return <ClueStorePage />;
      case Page.PrivacyPolicy:
        return <PrivacyPolicyPage />;
      case Page.TermsOfService:
        return <TermsOfServicePage />;
      case Page.Admin:
        return <AdminPage />;
      case Page.DailyChallenge:
        return <DailyChallengePage />;
      case Page.RecommendedGroups:
        return <RecommendedGroupsPage />;
      case Page.BlockedUsers:
        return <BlockedUsersPage />;
      case Page.PrivateGame:
        return <PrivateGamePage />;
      case Page.AIHome:
        return <AIHomePage />;
      case Page.AISupport:
        return <AISupportChatPage />;
      case Page.CreatePost:
        return <CreatePostPage {...props} />;
      case Page.ImageEditor:
        return <ImageEditorPage {...props} />;
      case Page.AboutUs:
        return <AboutUsPage />;
      case Page.AICreditStorePage:
        return <AICreditStorePage />;
      case Page.InviteFriends:
        return <InviteFriendsPage groupId={props?.groupId} />;
      case Page.ManageGroup:
        return <ManageGroupPage groupId={props?.groupId} />;
      default:
        return isNative ? <LoginPage /> : <LandingPage />;
    }
  };

  return (
    <PageContext.Provider
      value={{
        pageStack,
        switchPage,
        goBack,
        scrollToTop,
        launchEditProfileModal,
        updatePageStackProps,
        launchImageEditor,
        launchCelebrationModal,
        launchGuessersModal,
      }}
    >
      {!isReady && isNative && (
        <SplashScreenComponent onFinish={() => setIsReady(true)} />
      )}
      <div
        className={`h-[100dvh] w-full bg-gray-100 flex flex-col font-sans text-gray-900 overflow-hidden relative ${
          !isReady && isNative ? "hidden" : ""
        }`}
        style={
          {
            "--primary-dark-purple":
              userProfile?.primaryColor || DEFAULT_THEME_COLOR,
          } as any
        }
      >
        {!isFullScreen && <Header title={title} />}
        <main
          ref={mainRef}
          className="flex-grow relative overflow-y-auto custom-scrollbar"
        >
          <div className={isFullScreen ? "h-full" : "p-4 pb-40"}>
            {renderPage()}
          </div>
        </main>
        {!isFullScreen && !isKeyboardVisible && (
          <div className="fixed bottom-0 left-0 right-0 z-50 flex flex-col shadow-[0_-4px_10px_rgba(0,0,0,0.1)]">
            {/* Guest Mode Banner */}
            {!user && !loading && (
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-3 flex justify-between items-center px-4">
                <p className="font-bold text-sm">
                  Guest Mode{" "}
                  <span className="text-xs font-normal opacity-80">
                    Sign up to solve puzzles!
                  </span>
                </p>
                <button
                  onClick={() => switchPage(Page.Signup, "Sign Up")}
                  className="bg-white text-purple-700 font-bold px-4 py-2 rounded-full text-xs"
                >
                  Sign Up
                </button>
              </div>
            )}
            {/* Bottom Nav */}
            <BottomNav currentPage={page} />
            {/* Banner Ad */}
            <div className="w-full bg-white flex justify-center items-center border-t border-gray-200">
              <BannerAd />
            </div>
          </div>
        )}
        <Suspense fallback={null}>
          <EditProfileModal
            isOpen={isEditProfileModalOpen}
            onClose={() => setEditProfileModalOpen(false)}
            triggerRect={editProfileTriggerRect}
          />
          <CelebrationModal
            isOpen={!!celebrationProps}
            onClose={() => setCelebrationProps(null)}
            puzzle={celebrationProps?.puzzle as Puzzle}
            suggestions={celebrationProps?.suggestions}
          />
          <GuessersModal
            isOpen={!!guessersModalProps}
            onClose={() => setGuessersModalProps(null)}
            title={guessersModalProps?.title || ""}
            userIds={guessersModalProps?.userIds || []}
            extraInfo={guessersModalProps?.extraInfo}
          />
          <OnboardingTour
            isOpen={isOnboardingOpen}
            onComplete={() => {
              setOnboardingOpen(false);
              markOnboardingComplete();
            }}
          />
        </Suspense>
        <GlobalAdModals />
        <ToastContainer />
        <DebugConsole />
      </div>
    </PageContext.Provider>
  );
};

const App: React.FC = () => (
  <ErrorBoundary>
    <AppProvider>
      <ToastProvider>
        <AuthProvider>
          <AdminProvider>
            <PuzzleProvider>
              <ChatProvider>
                <AppContent />
              </ChatProvider>
            </PuzzleProvider>
          </AdminProvider>
        </AuthProvider>
      </ToastProvider>
    </AppProvider>
  </ErrorBoundary>
);

export default App;
